/**
 * RD & Company CRM - Warranty Management (Admin View) Layout
 */

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

// Authentication context
import { useAuth } from "contexts/AuthContext";

// API Configuration
import { API_CONFIG, getApiUrl } from "config/api";

function WarrantyManagement() {
  const { user, token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [expandedRequests, setExpandedRequests] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    status: "",
    admin_notes: "",
    resolution_notes: "",
    resolution_date: "",
    priority: "",
  });

  const toggleRequestExpansion = (requestId) => {
    setExpandedRequests((prev) => ({
      ...prev,
      [requestId]: !prev[requestId],
    }));
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getApiUrl(API_CONFIG.ENDPOINTS.WARRANTY)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch warranty requests");
      }

      const data = await response.json();
      setRequests(data.requests || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch(`${getApiUrl(API_CONFIG.ENDPOINTS.WARRANTY)}/statistics`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.statistics || {});
      }
    } catch (err) {
      console.error("Failed to fetch statistics:", err);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchStatistics();
  }, [token]);

  const openEditDialog = (request) => {
    setSelectedRequest(request);
    setFormData({
      status: request.status,
      admin_notes: request.admin_notes || "",
      resolution_notes: request.resolution_notes || "",
      resolution_date: request.resolution_date || "",
      priority: request.priority,
    });
    setIsEditOpen(true);
    setValidationErrors({});
    setError(null);
  };

  const closeEditDialog = () => {
    setIsEditOpen(false);
    setSelectedRequest(null);
    setFormData({
      status: "",
      admin_notes: "",
      resolution_notes: "",
      resolution_date: "",
      priority: "",
    });
    setValidationErrors({});
    setError(null);
  };

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (formData.resolution_date && new Date(formData.resolution_date) > new Date()) {
      errors.resolution_date = "Resolution date cannot be in the future";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateRequest = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${getApiUrl(API_CONFIG.ENDPOINTS.WARRANTY)}/${selectedRequest.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (data.success) {
        closeEditDialog();
        await fetchRequests();
        await fetchStatistics();
      } else {
        setError(data.message || "Failed to update warranty request");
        if (data.errors) {
          setValidationErrors(data.errors);
        }
      }
    } catch (err) {
      setError("Network error while updating warranty request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this warranty request?")) return;

    try {
      const response = await fetch(`${getApiUrl(API_CONFIG.ENDPOINTS.WARRANTY)}/${requestId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchRequests();
        await fetchStatistics();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.message || "Failed to delete warranty request");
      }
    } catch (err) {
      alert("Network error while deleting warranty request");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "under_review":
        return "info";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "completed":
        return "success";
      default:
        return "secondary";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "low":
        return "success";
      case "medium":
        return "warning";
      case "high":
        return "error";
      case "urgent":
        return "error";
      default:
        return "secondary";
    }
  };

  const filteredRequests = requests.filter((request) => {
    const matchesSearch =
      request.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.issue_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.request_type.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || request.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || request.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const columns = [
    { Header: "Client", accessor: "client", width: "15%" },
    { Header: "Product", accessor: "product", width: "18%" },
    { Header: "Type", accessor: "request_type", width: "8%" },
    { Header: "Priority", accessor: "priority", width: "8%" },
    { Header: "Status", accessor: "status", width: "10%" },
    { Header: "Date", accessor: "created_at", width: "10%" },
    {
      Header: "Actions",
      accessor: "actions",
      width: "12%",
      disableSortBy: true,
    },
  ];

  const rows = filteredRequests.map((request) => ({
    client: (
      <Box>
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {request.client_name}
        </MDTypography>
        <MDTypography variant="caption" color="text.secondary" display="block">
          {request.client_email}
        </MDTypography>
      </Box>
    ),
    product: (
      <Box>
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {request.product_name}
        </MDTypography>
        {request.serial_number && (
          <MDTypography variant="caption" color="text.secondary" display="block">
            SN: {request.serial_number}
          </MDTypography>
        )}
      </Box>
    ),
    request_type: (
      <Chip
        label={request.request_type.charAt(0).toUpperCase() + request.request_type.slice(1)}
        color="info"
        size="small"
      />
    ),
    priority: (
      <Chip
        label={request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
        color={getPriorityColor(request.priority)}
        size="small"
      />
    ),
    status: (
      <Chip
        label={
          request.status.replace("_", " ").charAt(0).toUpperCase() +
          request.status.replace("_", " ").slice(1)
        }
        color={getStatusColor(request.status)}
        size="small"
      />
    ),
    created_at: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {new Date(request.created_at).toLocaleDateString()}
      </MDTypography>
    ),
    actions: (
      <MDBox display="flex" alignItems="center">
        <MDButton
          variant="text"
          color="info"
          size="small"
          onClick={() => toggleRequestExpansion(request.id)}
        >
          <Icon>{expandedRequests[request.id] ? "expand_less" : "expand_more"}</Icon>
        </MDButton>
        <MDButton
          variant="text"
          color="primary"
          size="small"
          onClick={() => openEditDialog(request)}
        >
          <Icon>edit</Icon>
        </MDButton>
        <MDButton
          variant="text"
          color="error"
          size="small"
          onClick={() => handleDeleteRequest(request.id)}
        >
          <Icon>delete</Icon>
        </MDButton>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium" color="text">
                  Total Requests
                </MDTypography>
                <MDTypography variant="h4" fontWeight="bold" color="info">
                  {statistics.total_requests || 0}
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium" color="text">
                  Pending
                </MDTypography>
                <MDTypography variant="h4" fontWeight="bold" color="warning">
                  {statistics.pending_requests || 0}
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium" color="text">
                  Urgent
                </MDTypography>
                <MDTypography variant="h4" fontWeight="bold" color="error">
                  {statistics.urgent_requests || 0}
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium" color="text">
                  This Month
                </MDTypography>
                <MDTypography variant="h4" fontWeight="bold" color="success">
                  {statistics.requests_this_month || 0}
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <MDBox mb={3}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Warranty Management
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <MDInput
                    type="text"
                    placeholder="Search by client, product, or issue..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDInput
                    select
                    label="Filter by Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="under_review">Under Review</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </MDInput>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDInput
                    select
                    label="Filter by Priority"
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="all">All Priority</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </MDInput>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>

        <Card>
          <MDBox pt={3} px={3}>
            <MDTypography variant="h6" fontWeight="medium">
              All Warranty Requests
            </MDTypography>
          </MDBox>
          <MDBox pt={1} pb={2} px={3}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <MDTypography variant="body2" color="text">
                  Loading warranty requests...
                </MDTypography>
              </MDBox>
            ) : error ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <MDTypography variant="body2" color="error">
                  {error}
                </MDTypography>
                <MDButton variant="gradient" color="info" onClick={fetchRequests} sx={{ mt: 2 }}>
                  Retry
                </MDButton>
              </MDBox>
            ) : (
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            )}
          </MDBox>
        </Card>

        {/* Expanded Request Details */}
        {filteredRequests.map((request) => (
          <Collapse key={`details-${request.id}`} in={expandedRequests[request.id]}>
            <Card sx={{ mt: 2 }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Request Details
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="body2" color="text.secondary">
                      <strong>Client:</strong> {request.client_name} ({request.client_email})
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Product Name:</strong> {request.product_name}
                    </MDTypography>
                    {request.product_description && (
                      <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <strong>Description:</strong> {request.product_description}
                      </MDTypography>
                    )}
                    {request.serial_number && (
                      <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <strong>Serial Number:</strong> {request.serial_number}
                      </MDTypography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {request.purchase_date && (
                      <MDTypography variant="body2" color="text.secondary">
                        <strong>Purchase Date:</strong>{" "}
                        {new Date(request.purchase_date).toLocaleDateString()}
                      </MDTypography>
                    )}
                    {request.purchase_price && (
                      <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <strong>Purchase Price:</strong> ₹{request.purchase_price}
                      </MDTypography>
                    )}
                    <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Request Type:</strong>{" "}
                      {request.request_type.charAt(0).toUpperCase() + request.request_type.slice(1)}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Priority:</strong>{" "}
                      {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <MDTypography variant="body2" color="text.secondary">
                      <strong>Issue Description:</strong>
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      color="text"
                      sx={{ mt: 1, p: 2, bgcolor: "grey.50", borderRadius: 1 }}
                    >
                      {request.issue_description}
                    </MDTypography>
                  </Grid>
                  {request.admin_notes && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <MDTypography variant="body2" color="text.secondary">
                        <strong>Admin Notes:</strong>
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        color="text"
                        sx={{
                          mt: 1,
                          p: 2,
                          bgcolor: "info.light",
                          borderRadius: 1,
                        }}
                      >
                        {request.admin_notes}
                      </MDTypography>
                    </Grid>
                  )}
                  {request.resolution_notes && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <MDTypography variant="body2" color="text.secondary">
                        <strong>Resolution Notes:</strong>
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        color="text"
                        sx={{
                          mt: 1,
                          p: 2,
                          bgcolor: "success.light",
                          borderRadius: 1,
                        }}
                      >
                        {request.resolution_notes}
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Collapse>
        ))}
      </MDBox>

      {/* Edit Warranty Request Dialog */}
      <Dialog open={isEditOpen} onClose={closeEditDialog} fullWidth maxWidth="md">
        <DialogTitle>Update Warranty Request</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            {/* Display general error message */}
            {error && (
              <MDBox mb={2}>
                <MDTypography variant="body2" color="error">
                  {error}
                </MDTypography>
              </MDBox>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDInput
                    select
                    label="Status *"
                    value={formData.status}
                    onChange={(e) => updateField("status", e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="under_review">Under Review</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </MDInput>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDInput
                    select
                    label="Priority"
                    value={formData.priority}
                    onChange={(e) => updateField("priority", e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </MDInput>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Admin Notes"
                    value={formData.admin_notes}
                    onChange={(e) => updateField("admin_notes", e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Resolution Notes"
                    value={formData.resolution_notes}
                    onChange={(e) => updateField("resolution_notes", e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDInput
                    type="date"
                    label="Resolution Date"
                    value={formData.resolution_date}
                    onChange={(e) => updateField("resolution_date", e.target.value)}
                    fullWidth
                    error={!!validationErrors.resolution_date}
                    InputLabelProps={{ shrink: true }}
                  />
                  {validationErrors.resolution_date && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {validationErrors.resolution_date}
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={closeEditDialog} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleUpdateRequest} color="info" disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Update Request"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default WarrantyManagement;
